// v2 quarry — a stepped opencast working face: a high rock bench across the back
// with side wings stepping down over a mid bench to a dark pit floor that opens
// toward the road, a timber crane on the back rim (mast, stayed jib reaching over
// the pit, a dressed block swinging on its hoist rope), a half-cut block still on
// the bench, finished block stacks and rubble at the front. Ported 1:1 from
// age-of-mcp's v2/quarry.ts, using the library's shared `foundationSlab` and
// `scaffold` helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, benchWork, EFFECT_LOOP, type Part } from "../../lib";

const ROCK   = "#7d7a73";   // worked stone
const ROCKD  = "#5d5a54";   // darker terrace ledge
const PIT    = "#2a2825";   // pit floor
const TIMBER = "#6e4d2c";   // crane timber
const BLOCK  = "#9a968c";   // dressed blocks
const ROPE   = "#3a2c1c";

const BASE = 0.14;          // foundation slab top
const RIM  = BASE + 0.42;   // top of the back bench — 0.56

// The stepped rock terraces: back bench high, side wings lower, mid bench
// lower again, pit floor at the front. `low` builds the early flat version.
function terraces(low: boolean): Part[] {
  if (low) {
    return [
      { geometry: new THREE.BoxGeometry(0.78, 0.20, 0.34), color: ROCKD,
        position: [0, BASE + 0.10, -0.22] },
      { geometry: new THREE.BoxGeometry(0.20, 0.14, 0.42), color: ROCKD,
        position: [-0.29, BASE + 0.07, 0.13] },
      { geometry: new THREE.BoxGeometry(0.20, 0.14, 0.42), color: ROCKD,
        position: [0.29, BASE + 0.07, 0.13] },
    ];
  }
  return [
    { geometry: new THREE.BoxGeometry(0.78, 0.42, 0.34), color: ROCK,
      position: [0, BASE + 0.21, -0.22] },
    { geometry: new THREE.BoxGeometry(0.20, 0.30, 0.42), color: ROCKD,
      position: [-0.29, BASE + 0.15, 0.13] },
    { geometry: new THREE.BoxGeometry(0.20, 0.30, 0.42), color: ROCK,
      position: [0.29, BASE + 0.15, 0.13] },
    { geometry: new THREE.BoxGeometry(0.36, 0.18, 0.22), color: ROCKD,
      position: [0, BASE + 0.09, 0.04] },
    { geometry: new THREE.BoxGeometry(0.36, 0.06, 0.20), color: PIT,
      position: [0, BASE + 0.03, 0.30] },
  ];
}

// The timber crane on the back rim: a mast, a stayed jib leaning out over the
// pit, and a dressed block swinging on the hoist rope.
function crane(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.035, 0.045, 0.52, 6), color: TIMBER,
      position: [-0.24, RIM + 0.26, -0.20] },
    { geometry: new THREE.BoxGeometry(0.05, 0.05, 0.52), color: TIMBER,
      position: [-0.24, 0.952, 0.039], rotation: [0.42, 0, 0] },
    { geometry: new THREE.CylinderGeometry(0.010, 0.010, 0.275, 4), color: ROPE,
      position: [-0.24, 1.015, -0.08], rotation: [1.07, 0, 0] },
    // Hoist rope and swinging block are named so the "active" animation can
    // wind them up and down (the rope shortens about its top end at the jib).
    { geometry: new THREE.CylinderGeometry(0.010, 0.010, 0.42, 4), color: ROPE,
      position: [-0.24, 0.62, 0.27], name: "hoist-rope" },
    { geometry: new THREE.BoxGeometry(0.12, 0.10, 0.12), color: BLOCK,
      position: [-0.24, 0.35, 0.27], rotation: [0, 0.25, 0], name: "hoist-load" },
  ];
}

// The half-cut block still attached on the mid bench, the finished stacks at
// the east front corner, and rubble at the west.
function blocks(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.13, 0.09, 0.11), color: BLOCK,
      position: [0.08, BASE + 0.225, 0.04], rotation: [0, 0.15, 0] },
    { geometry: new THREE.BoxGeometry(0.18, 0.12, 0.18), color: BLOCK,
      position: [0.32, BASE + 0.06, 0.32] },
    { geometry: new THREE.BoxGeometry(0.15, 0.10, 0.15), color: BLOCK,
      position: [0.32, BASE + 0.17, 0.32], rotation: [0, 0.3, 0] },
    { geometry: new THREE.BoxGeometry(0.14, 0.10, 0.14), color: BLOCK,
      position: [0.12, BASE + 0.05, 0.34], rotation: [0, -0.2, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.07, 0), color: ROCKD,
      position: [-0.31, BASE + 0.06, 0.36], rotation: [0.2, 0.8, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.05, 0), color: ROCK,
      position: [-0.22, BASE + 0.04, 0.39], rotation: [0.5, 1.6, 0.2] },
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.84)];
  if (s === 1) return [foundationSlab(0.84), ...terraces(true), ...scaffold(0.82, 0.5)];
  if (s === 2) return [foundationSlab(0.84), ...terraces(false), ...scaffold(0.82, 0.8)];
  return [
    foundationSlab(0.84),
    ...terraces(false),
    ...crane(),
    ...blocks(),
  ];
}

export const quarryV2 = defineModel({
  id: "quarry-v2",
  tiles: { w: 1, h: 1 },
  batchId: "fable",
  name: "Quarry (v2)",
  tags: ["building","quarry","low-poly","age-of-mcp","fable","animated"],
  summary: "Low-poly quarry.",
  description: "A worked stone pit that extracts stone on a slow timer.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      // The crane hauls the block up out of the pit — it rises 0.24 with a
      // slow twist on the rope, while the rope winds up about its top end at
      // the jib (y = 0.83) to stay taut, then lowers.
      id: "active",
      label: "Crane hoisting",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [
        {
          target: "hoist-load",
          pivot: [-0.24, 0.35, 0.27],
          position: [
            { t: 0, value: [0, 0, 0] },
            { t: EFFECT_LOOP / 2, value: [0, 0.24, 0], ease: "easeInOut" },
            { t: EFFECT_LOOP, value: [0, 0, 0], ease: "easeInOut" },
          ],
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: EFFECT_LOOP / 2, value: [0, 0.35, 0], ease: "sine" },
            { t: EFFECT_LOOP, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "hoist-rope",
          pivot: [-0.24, 0.83, 0.27],
          scale: [
            { t: 0, value: [1, 1, 1] },
            { t: EFFECT_LOOP / 2, value: [1, 0.5, 1], ease: "easeInOut" },
            { t: EFFECT_LOOP, value: [1, 1, 1], ease: "easeInOut" },
          ],
        },
      ],
    },
  ],
  stations: [
    { id: "bench", position: [0.14, 0.56, -0.2], facing: 0 },
  ],
  interactions: [
    { id: "cut", label: "Cutting stone", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
